import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useLayoutEffect, useRef, useState } from 'react';
import { Box, Icon, IconButton, Typography, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
const SEVERITY_STYLES = {
    warning: { background: '#FDECC8', border: '#F0C36D', color: '#7A5B00', icon: 'warning_amber' },
    info: { background: '#E3F1FC', border: '#90CAF9', color: '#0B4F8A', icon: 'info' },
    error: { background: '#FBE7E7', border: '#F1A9A9', color: '#8A1F1F', icon: 'error_outline' },
    success: { background: '#E4F5E7', border: '#A3D9AE', color: '#1E6B33', icon: 'check_circle_outline' },
};
const SystemBanner = ({ title, message, severity = 'warning', icon, dismissible = true, sticky = true, backgroundColor, textColor, iconColor, fontSize, fontFamily, paddingTop = 0.625, paddingBottom = 0.625, paddingLeft = 1.5, paddingRight = 1.5, scrollableMessage = false, sx, }) => {
    const theme = useTheme();
    const [visible, setVisible] = useState(true);
    const preset = SEVERITY_STYLES[severity] || SEVERITY_STYLES.warning;
    const iconName = icon && icon.trim() !== '' ? icon.trim() : preset.icon;
    const resolvedTextColor = textColor || preset.color;
    /* Where the banner naturally sits in the page (i.e. right below whatever precedes it — usually
       the navbar) — measured from the invisible placeholder below, since `position: fixed` needs an
       explicit `top` and this component has no other way to know the preceding navbar's height. */
    const placeholderRef = useRef(null);
    const [fixedTop, setFixedTop] = useState(null);
    useLayoutEffect(() => {
        if (!sticky)
            return;
        const measure = () => {
            const el = placeholderRef.current;
            if (el)
                setFixedTop(el.getBoundingClientRect().top);
        };
        measure();
        window.addEventListener('resize', measure);
        return () => window.removeEventListener('resize', measure);
    }, [sticky]);
    if (!visible)
        return null;
    const rowSx = {
        width: '100%',
        margin: 0,
        display: 'flex',
        alignItems: { xs: 'flex-start', sm: 'center' },
        gap: { xs: 0.75, sm: 1.25 },
        backgroundColor: backgroundColor || preset.background,
        borderLeftWidth: '3px',
        borderLeftStyle: 'solid',
        borderLeftColor: resolvedTextColor,
        color: resolvedTextColor,
        fontFamily: fontFamily || theme.typography.fontFamily,
        paddingTop,
        paddingBottom,
        paddingLeft,
        paddingRight,
    };
    const content = (_jsxs(_Fragment, { children: [_jsx(Box, { sx: {
                    flexShrink: 0,
                    width: 28,
                    height: 28,
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: alpha(theme.palette.common.black, 0.06),
                    mt: { xs: 0.15, sm: 0 },
                }, children: _jsx(Icon, { sx: { color: iconColor || 'inherit', fontSize: '1.1rem' }, children: iconName }) }), _jsxs(Box, { sx: {
                    flex: 1,
                    minWidth: 0,
                    display: 'grid',
                    gridTemplateColumns: {
                        xs: '1fr',
                        sm: title && message ? 'auto auto minmax(0, 1fr)' : 'auto minmax(0, 1fr)',
                    },
                    alignItems: { xs: 'flex-start', sm: 'center' },
                    columnGap: 1.25,
                    rowGap: 0.25,
                }, children: [title && (_jsx(Typography, { component: "span", sx: { fontWeight: 600, fontSize: fontSize || '0.875rem', color: 'inherit', letterSpacing: '0.005em', whiteSpace: 'nowrap' }, children: title })), title && message && (_jsx(Box, { "aria-hidden": true, sx: { width: '1px', alignSelf: 'stretch', bgcolor: 'currentColor', opacity: 0.25, display: { xs: 'none', sm: 'block' } } })), _jsx(Box, { sx: {
                            fontSize: fontSize || '0.8125rem',
                            color: 'inherit',
                            opacity: 0.92,
                            minWidth: 0,
                            ...(scrollableMessage
                                ? { overflow: 'hidden', whiteSpace: 'nowrap' }
                                : {}),
                        }, children: scrollableMessage ? (_jsxs(Box, { component: "span", sx: {
                                display: 'inline-flex',
                                whiteSpace: 'nowrap',
                                width: 'max-content',
                                animation: 'system-banner-marquee 16s linear infinite',
                                '@keyframes system-banner-marquee': {
                                    '0%': { transform: 'translateX(0)' },
                                    '100%': { transform: 'translateX(-50%)' },
                                },
                            }, children: [_jsx(Box, { component: "span", sx: { pr: 6 }, children: message }), _jsx(Box, { component: "span", "aria-hidden": true, sx: { pr: 6 }, children: message })] })) : (_jsx(Typography, { component: "span", sx: { fontSize: 'inherit', color: 'inherit' }, children: message })) })] }), dismissible && (_jsx(IconButton, { size: "small", onClick: () => setVisible(false), "aria-label": "Dismiss", sx: {
                    color: 'inherit',
                    flexShrink: 0,
                    p: 0.5,
                    borderRadius: '50%',
                    transition: 'background-color 0.15s ease, transform 0.15s ease',
                    '&:hover': { bgcolor: alpha(theme.palette.common.black, 0.08), transform: 'scale(1.08)' },
                    '&:active': { transform: 'scale(0.95)' },
                }, children: _jsx(CloseIcon, { fontSize: "small" }) }))] }));
    const entranceSx = {
        animation: 'system-banner-in 0.28s ease-out',
        '@keyframes system-banner-in': {
            from: { opacity: 0, transform: 'translateY(-6px)' },
            to: { opacity: 1, transform: 'translateY(0)' },
        },
    };
    if (!sticky) {
        return (_jsx(Box, { component: "section", role: "status", sx: { ...rowSx, ...entranceSx, position: 'relative', zIndex: 1, ...sx }, children: content }));
    }
    /* `position: sticky` doesn't work here — this app's html/body/#root deliberately set
       `overflow-x: clip` everywhere (to contain horizontal overflow), which makes them the sticky
       positioning container instead of the viewport, so it never actually catches. `position: fixed`
       isn't affected by that (no ancestor here establishes a transformed containing block), so pin
       with fixed instead — paired with an invisible same-size placeholder that reserves its spot in
       normal flow (a fixed element takes up no document space itself) and tells us, via its own
       natural position, how far below the top of the viewport the banner should pin (usually the
       navbar's height) so the fixed banner never paints over whatever precedes it. */
    return (_jsxs(_Fragment, { children: [_jsx(Box, { component: "section", role: "status", sx: {
                    ...rowSx,
                    ...entranceSx,
                    position: 'fixed',
                    top: fixedTop ?? 0,
                    left: 0,
                    right: 0,
                    zIndex: theme.zIndex.appBar + 1,
                    boxShadow: `0 4px 16px ${alpha(theme.palette.common.black, 0.1)}`,
                    visibility: fixedTop === null ? 'hidden' : 'visible',
                    ...sx,
                }, children: content }), _jsx(Box, { ref: placeholderRef, "aria-hidden": true, sx: { ...rowSx, visibility: 'hidden', pointerEvents: 'none' }, children: content })] }));
};
export default SystemBanner;
